import { Component, inject, OnInit, viewChild } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { IFormsProvider, LiveForm, UserInformation } from '@lfw/roesberg.liveforms.core';
import { LiveformsEditorComponent } from '@lfw/roesberg.liveforms.editor';
import { TranslatePipe } from '@ngx-translate/core';
import { of, take, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { LfwRouterService } from '../../../route/lfw-router.service';
import { AppToolbarButtonComponent } from '../../../shared/components/app-toolbar-button/app-toolbar-button.component';
import { AppToolbarDividerComponent } from '../../../shared/components/app-toolbar-divider/app-toolbar-divider.component';
import { AppToolbarTextComponent } from '../../../shared/components/app-toolbar-text/app-toolbar-text.component';
import { DefaultDialogService } from '../../../shared/services/default-dialog.service';
import { SettingsService } from '../../../shared/settings/settings.service';
import { UserInformationService } from '../../../shell/user-information.service';
import { FormViewToolbarComponent } from '../../components/form-toolbar/form-view-toolbar.component';
import { SubStateSelectComponent } from '../../components/sub-state-select/sub-state-select.component';
import { OrdersService } from '../../orders.service';
import { OnlineFormsProvider } from '../online-forms.provider';

@Component({
  selector: 'lfw-online-form-view',
  templateUrl: './online-order-view.component.html',
  styleUrl: './online-order-view.component.css',
  providers: [{ provide: IFormsProvider, useClass: OnlineFormsProvider }],
  imports: [
    LiveformsEditorComponent,
    FormViewToolbarComponent,
    AppToolbarButtonComponent,
    AppToolbarDividerComponent,
    AppToolbarTextComponent,
    SubStateSelectComponent,
    TranslatePipe,
    MatIcon,
    MatMenu,
    MatMenuTrigger,
    MatMenuItem
  ]
})
export class OnlineOrderViewComponent implements OnInit {
  private readonly userInfoService = inject(UserInformationService);
  private readonly routerService = inject(LfwRouterService);
  private readonly orderService = inject(OrdersService);
  private readonly route = inject(ActivatedRoute);
  private readonly dialogService = inject(DefaultDialogService);
  private readonly settingsService = inject(SettingsService);
  private readonly title = inject(Title);

  form: LiveForm | undefined = undefined;

  user: UserInformation = this.userInfoService.userInformation;
  readonly editor = viewChild.required(LiveformsEditorComponent);
  formsProvider = inject(IFormsProvider);
  editorSettings = this.settingsService.getEditorSettings();

  ngOnInit(): void {
    this.formsProvider
      .loadForm()
      .pipe(
        take(1),
        tap((form: LiveForm) => {
          this.form = form;
          this.title.setTitle(this.form.order.text);
        }),
        catchError((error) => {
          this.error(this.route.snapshot.params['form']);
          return throwError(() => error);
        })
      )
      .subscribe();
  }

  error(formId: string): void {
    this.dialogService.formNotFound(formId);
  }

  get caption(): string {
    if (this.form?.order.text) {
      this.title.setTitle(this.form?.order.text);
    }
    return this.form?.order.text ?? '';
  }

  get viewVisible(): boolean {
    return true; // this.form != undefined && this.form.id != '';
  }

  close(): void {
    this.routerService.navigateToOrdersRoot();
  }

  userCanEdit(): boolean {
    if (!this.form || !this.form.order) {
      return false;
    }
    return this.form.order.canEdit();
  }

  finalize(): void {
    if (!this.form || !this.editor) {
      return;
    }
    of(this.form)
      .pipe(
        switchMap((form) => {
          if (!this.editor) {
            return of(form);
          }
          return this.editor()
            .save()
            .pipe(map(() => form));
        }),
        switchMap((form) => this.orderService.finishOrder(form.project.id, form.order.id)),
        catchError((error) => {
          this.dialogService.showSaveError(error);
          return error;
        }),
        take(1)
      )
      .subscribe();
  }

  editForm(): void {
    if (!this.form) {
      return;
    }
    this.routerService.navigate(['../..', 'edit', this.form?.id], { relativeTo: this.route, replaceUrl: true });
  }
}
